import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import Card from '../design-system/Card';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * /profile — V1 account info surface.
 *
 * Sections (top to bottom):
 *   1. Avatar    — upload to profile-photos/{user_id}/avatar.{ext},
 *                  URL persisted to user_metadata.avatar_url
 *   2. Display name — editable inline; persisted to user_metadata.display_name
 *                     (sidebar prefers display_name → full_name → email prefix)
 *   3. Email     — read-only with Verified badge; "Change" opens an inline
 *                  form that calls supabase.auth.updateUser({ email }).
 *                  Supabase fires both old + new email confirmations; we just
 *                  show a banner.
 *   4. Account info — Member since, Skills tracked (count), Plan: Beta.
 *
 * Auth state changes propagate to the sidebar automatically via the
 * onAuthStateChange listener in App.js — nothing extra to wire here.
 */
export default function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [skillsCount, setSkillsCount] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'ok' | 'err', message }
  const [emailBanner, setEmailBanner] = useState(null); // string

  /* MOUNT */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (cancelled) return;
        if (error) throw error;
        setUser(data?.user || null);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Could not load your profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for downstream auth updates (USER_UPDATED fires after
  // updateUser completes) so the page reflects fresh data immediately.
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) setUser(session.user);
    });
    return () => listener?.subscription?.unsubscribe?.();
  }, []);

  // Skills count — distinct skill_name values for current user.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('skill_scores')
          .select('skill_name')
          .eq('user_id', user.id);
        if (cancelled) return;
        const distinct = new Set((data || []).map((r) => r.skill_name));
        setSkillsCount(distinct.size);
      } catch (_) {
        if (!cancelled) setSkillsCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const showToast = (message, type = 'ok') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2200);
  };

  const refreshUser = async () => {
    const { data } = await supabase.auth.getUser();
    setUser(data?.user || null);
  };

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <div style={pageWrapStyle}>
        <PageHeader />
        <LoadingState rows={4} label="Loading profile" />
      </div>
    );
  }
  if (loadError || !user) {
    return (
      <div style={pageWrapStyle}>
        <PageHeader />
        <ErrorState
          title="Couldn't load your profile"
          message={loadError || 'You may need to sign in again.'}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const displayName = currentDisplayName(user);
  const avatarUrl = user?.user_metadata?.avatar_url || null;
  const memberSince = formatJoinedDate(user?.created_at);
  const isVerified = !!user?.email_confirmed_at || !!user?.confirmed_at;

  return (
    <div style={pageWrapStyle}>
      <PageHeader />

      <AvatarUpload
        userId={user.id}
        avatarUrl={avatarUrl}
        displayName={displayName}
        onChanged={async () => { await refreshUser(); }}
        onToast={showToast}
      />

      <DisplayNameCard
        userId={user.id}
        currentName={displayName}
        onSaved={async () => {
          await refreshUser();
          showToast('Saved');
        }}
        onToast={showToast}
      />

      <EmailCard
        currentEmail={user.email || ''}
        isVerified={isVerified}
        onBanner={setEmailBanner}
        onToast={showToast}
      />

      {emailBanner && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: radius.md,
            background: 'rgba(47, 91, 255, 0.06)',
            border: `1px solid rgba(47, 91, 255, 0.16)`,
            color: colors.textPrimary,
            fontSize: 14,
            fontFamily: fontStack,
            lineHeight: 1.5,
          }}
        >
          {emailBanner}
        </div>
      )}

      <AccountInfoCard
        memberSince={memberSince}
        skillsCount={skillsCount}
      />

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}

/* ================================================================
   Page header
   ================================================================ */

function PageHeader() {
  return (
    <header style={{ fontFamily: fontStack }}>
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 600,
          color: colors.textPrimary,
          letterSpacing: '-0.01em',
        }}
      >
        Profile
      </h1>
      <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textSecondary }}>
        Manage your account info.
      </p>
    </header>
  );
}

/* ================================================================
   Avatar upload
   ================================================================ */

function AvatarUpload({ userId, avatarUrl, displayName, onChanged, onToast }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      setError('Please choose a JPG, PNG, or WEBP.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please choose a JPG, PNG, or WEBP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB.');
      return;
    }

    setError('');
    setBusy(true);
    try {
      const ext = mimeToExt(file.type);
      const path = `${userId}/avatar.${ext}`;

      // Upsert so re-uploads overwrite.
      const { error: upErr } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from('profile-photos').getPublicUrl(path);
      // Cache-bust so the browser doesn't keep showing the previous file.
      const publicUrl = `${pub.publicUrl}?v=${Date.now()}`;

      const { error: metaErr } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl },
      });
      if (metaErr) throw metaErr;

      onChanged && (await onChanged());
      onToast && onToast('Photo updated');
    } catch (err) {
      setError(err?.message || 'Upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!avatarUrl) return;
    if (!window.confirm('Remove your profile photo?')) return;
    setBusy(true);
    setError('');
    try {
      // Best-effort: try every supported extension. Storage will
      // silently no-op if a file is absent.
      await Promise.all(
        ['jpg', 'jpeg', 'png', 'webp'].map((ext) =>
          supabase.storage.from('profile-photos').remove([`${userId}/avatar.${ext}`]).catch(() => null)
        )
      );

      const { error: metaErr } = await supabase.auth.updateUser({
        data: { avatar_url: null },
      });
      if (metaErr) throw metaErr;

      onChanged && (await onChanged());
      onToast && onToast('Photo removed');
    } catch (err) {
      setError(err?.message || 'Could not remove photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionLabel>Avatar</SectionLabel>
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <Avatar size={80} avatarUrl={avatarUrl} displayName={displayName} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={handlePick} disabled={busy} style={primaryBtn(busy)}>
              {busy ? 'Uploading…' : avatarUrl ? 'Change photo' : 'Upload photo'}
            </button>
            {avatarUrl && (
              <button type="button" onClick={handleRemove} disabled={busy} style={ghostBtn}>
                Remove
              </button>
            )}
          </div>
          <span style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fontStack }}>
            PNG / JPG / WEBP, max 2MB.
          </span>
          {error && <p style={inlineErrorStyle}>{error}</p>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </div>
    </Card>
  );
}

function Avatar({ size = 80, avatarUrl, displayName }) {
  const initial = (displayName?.[0] || '?').toUpperCase();
  const common = {
    width: size, height: size,
    borderRadius: '50%',
    flexShrink: 0,
  };
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName ? `${displayName}'s avatar` : 'avatar'}
        style={{
          ...common,
          objectFit: 'cover',
          border: `1px solid ${colors.border}`,
        }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        ...common,
        background: colors.primary,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: fontStack,
        fontWeight: 700,
        fontSize: size * 0.4,
        letterSpacing: '0.01em',
      }}
    >
      {initial}
    </div>
  );
}

/* ================================================================
   Display name (editable inline)
   ================================================================ */

const NAME_RE = /^[\p{L}\p{M}\p{N} \-'.]+$/u;

function DisplayNameCard({ userId, currentName, onSaved, onToast }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Sync editor with prop when it changes (e.g. after refreshUser).
  useEffect(() => { if (!editing) setValue(currentName); }, [currentName, editing]);

  const start = () => {
    setValue(currentName);
    setError('');
    setEditing(true);
  };
  const cancel = () => {
    setValue(currentName);
    setError('');
    setEditing(false);
  };

  const save = async () => {
    const trimmed = String(value || '').trim();
    if (!trimmed) { setError('Name is required.'); return; }
    if (trimmed.length > 50) { setError('Name is too long (max 50).'); return; }
    if (!NAME_RE.test(trimmed)) {
      setError("Letters, numbers, spaces, hyphens, apostrophes only.");
      return;
    }
    if (trimmed === currentName) { setEditing(false); return; }
    setSaving(true); setError('');
    try {
      const { error: e } = await supabase.auth.updateUser({
        data: { display_name: trimmed },
      });
      if (e) throw e;
      setEditing(false);
      onSaved && (await onSaved());
    } catch (err) {
      setError(err?.message || 'Could not save name.');
      onToast && onToast(err?.message || 'Save failed', 'err');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionLabel>Display name</SectionLabel>
      <div style={{ marginTop: 10 }}>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, 50))}
              autoFocus
              maxLength={50}
              style={{ ...inputStyle, ...(error ? inputErrorBorder : null) }}
            />
            {error && <p style={inlineErrorStyle}>{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={save} disabled={saving} style={primaryBtn(saving)}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={cancel} disabled={saving} style={ghostBtn}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={rowStyle}>
            <span style={{ fontSize: 16, fontWeight: 500, color: colors.textPrimary }}>
              {currentName}
            </span>
            <button type="button" onClick={start} style={smallGhostBtn}>Edit</button>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ================================================================
   Email (change flow)
   ================================================================ */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailCard({ currentEmail, isVerified, onBanner, onToast }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const next = String(value || '').trim().toLowerCase();
    if (!EMAIL_RE.test(next)) { setError('Please enter a valid email address.'); return; }
    if (next === String(currentEmail || '').toLowerCase()) {
      setError("That's your current email.");
      return;
    }
    setSaving(true); setError('');
    try {
      const { error: e2 } = await supabase.auth.updateUser({ email: next });
      if (e2) throw e2;
      setEditing(false);
      setValue('');
      onBanner && onBanner(`Confirmation emails sent. Check both ${currentEmail} and ${next} (including spam).`);
      onToast && onToast('Confirmation sent');
    } catch (err) {
      setError(err?.message || 'Could not request email change.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionLabel>Email</SectionLabel>
      {editing ? (
        <form onSubmit={submit} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            placeholder="new@example.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            required
            style={{ ...inputStyle, ...(error ? inputErrorBorder : null) }}
          />
          <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, fontFamily: fontStack, lineHeight: 1.5 }}>
            You'll need to confirm both your current and new email.
          </p>
          {error && <p style={inlineErrorStyle}>{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              disabled={saving}
              style={primaryBtn(saving)}
            >
              {saving ? 'Sending…' : 'Send confirmation'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setValue(''); setError(''); }}
              disabled={saving}
              style={ghostBtn}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div style={{ ...rowStyle, marginTop: 10 }}>
            <span style={{ fontSize: 16, color: colors.textPrimary, fontFamily: fontStack, wordBreak: 'break-all' }}>
              {currentEmail}
            </span>
            <button type="button" onClick={() => setEditing(true)} style={smallGhostBtn}>Change</button>
          </div>
          {isVerified && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: 'var(--success)',
                fontFamily: fontStack,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <CheckGlyph /> Verified
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ================================================================
   Account info (read-only)
   ================================================================ */

function AccountInfoCard({ memberSince, skillsCount }) {
  return (
    <Card padding={spacing.lg}>
      <SectionLabel>Account info</SectionLabel>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <KV label="Member since" value={memberSince} />
        <KV label="Skills tracked" value={skillsCount === null ? '—' : `${skillsCount}`} />
        <KV label="Plan" value={<PlanBadge>Beta</PlanBadge>} />
      </div>
    </Card>
  );
}

function KV({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        fontFamily: fontStack,
      }}
    >
      <span style={{ fontSize: 13, color: colors.textSecondary }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: colors.textPrimary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PlanBadge({ children }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: 999,
        backgroundColor: 'rgba(47, 91, 255, 0.10)',
        color: colors.primary,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </span>
  );
}

/* ================================================================
   Tiny shared bits + glyphs + styles
   ================================================================ */

function SectionLabel({ children }) {
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 12,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        fontWeight: 600,
        fontFamily: fontStack,
      }}
    >
      {children}
    </h2>
  );
}

function CheckGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Toast({ message, type }) {
  const isError = type === 'err';
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 18px',
        borderRadius: 999,
        backgroundColor: isError ? 'var(--danger)' : colors.textPrimary,
        color: '#fff',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: fontStack,
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
        zIndex: 220,
      }}
    >
      {message}
    </div>
  );
}

const pageWrapStyle = {
  maxWidth: 800,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.base,
  fontFamily: fontStack,
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  minHeight: 44,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 15,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const inputErrorBorder = { borderColor: 'var(--danger)' };

const inlineErrorStyle = {
  margin: 0,
  color: 'var(--danger)',
  fontSize: 12,
  fontFamily: fontStack,
};

const primaryBtn = (busy) => ({
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  border: 'none',
  background: busy ? '#9CB1FF' : colors.primary,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: busy ? 'wait' : 'pointer',
});

const ghostBtn = {
  padding: '10px 16px',
  minHeight: 44,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const smallGhostBtn = {
  padding: '6px 14px',
  minHeight: 32,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

/* ================================================================
   Helpers exported so other parts of the app can stay consistent
   on display-name resolution and avatar rendering.
   ================================================================ */

export function currentDisplayName(user) {
  return (
    user?.user_metadata?.display_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Account'
  );
}

function formatJoinedDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_) {
    return '—';
  }
}

function mimeToExt(mime) {
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}
